import React, { useState, useRef } from 'react';
import { FileText, Download, Printer, CircleAlert } from 'lucide-react';
import { ReportConfigModal, ReportConfig } from './ReportConfigModal';
import { ReportPreview } from './ReportPreview';
import { Stakeholder, Assignment } from '../../types/stakeholder';
import { Organization } from '../../types/organization';
import { MediaAsset } from '../../types/media';
import { WikiPage } from '../../types/wiki';
import { InfluenceScore, InfluenceCategory } from '../../services/influencesService';
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader } from '../ui';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface ReportGeneratorProps {
  entity: Stakeholder | Organization;
  entityType: 'stakeholder' | 'organization';
  assignments?: Assignment[];
  media?: MediaAsset[];
  wikiPages?: WikiPage[];
  influenceScores?: InfluenceScore[];
  influenceCategories?: InfluenceCategory[];
  stakeholders?: Stakeholder[]; // For organization reports
}

export const ReportGenerator: React.FC<ReportGeneratorProps> = ({
  entity,
  entityType,
  assignments = [],
  media = [],
  wikiPages = [],
  influenceScores = [],
  influenceCategories = [],
  stakeholders = []
}) => {
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [config, setConfig] = useState<ReportConfig | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const reportRef = useRef<HTMLDivElement>(null);

  const handleConfigSubmit = (newConfig: ReportConfig) => {
    setConfig(newConfig);
    setShowConfigModal(false);
    setShowPreview(true);
  };

  const generatePDF = async () => {
    if (!reportRef.current || !config) return;

    try {
      setIsGenerating(true);
      setError(null);

      // Create a new canvas element
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: reportRef.current.scrollWidth,
        height: reportRef.current.scrollHeight,
        scrollX: 0,
        scrollY: 0
      });

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -pageHeight + heightLeft, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generate filename
      const entityName = entityType === 'stakeholder' 
        ? `${(entity as Stakeholder).firstName}_${(entity as Stakeholder).lastName}`
        : (entity as Organization).name;
      const filename = `${entityName}_Report_${new Date().toISOString().split('T')[0]}.pdf`;

      // Download PDF
      pdf.save(filename);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = () => {
    if (!reportRef.current) return;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Unable to open print window. Please check your popup blocker.');
      return;
    }

    // Get the report content
    const reportContent = reportRef.current.innerHTML;
    
    // Create print-friendly HTML
    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${entityType === 'stakeholder' ? 'Stakeholder' : 'Organization'} Report</title>
          <style>
            @media print {
              @page {
                margin: 0.5in;
                size: A4;
              }
              body {
                font-family: system-ui, -apple-system, sans-serif;
                line-height: 1.6;
                color: #1f2937;
                margin: 0;
                padding: 0;
              }
              .print-break {
                page-break-before: always;
              }
              .no-print {
                display: none !important;
              }
              table {
                page-break-inside: avoid;
              }
              h1, h2, h3 {
                page-break-after: avoid;
              }
            }
            body {
              font-family: system-ui, -apple-system, sans-serif;
              line-height: 1.6;
              color: #1f2937;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .print-controls {
              position: fixed;
              top: 10px;
              right: 10px;
              background: white;
              padding: 10px;
              border: 1px solid #ccc;
              border-radius: 4px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .print-controls button {
              margin: 0 5px;
              padding: 8px 16px;
              border: 1px solid #ccc;
              background: white;
              cursor: pointer;
              border-radius: 4px;
            }
            .print-controls button:hover {
              background: #f5f5f5;
            }
            .print-controls button.primary {
              background: #ea2a33;
              color: white;
              border-color: #ea2a33;
            }
            .print-controls button.primary:hover {
              background: #b91c1c;
            }
          </style>
        </head>
        <body>
          <div class="print-controls no-print">
            <button onclick="window.print()" class="primary">Print</button>
            <button onclick="window.close()">Close</button>
          </div>
          <div class="report-content">
            ${reportContent}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    // Focus the print window
    printWindow.focus();
  };

  const handleGenerate = async () => {
    if (!config) return;

    if (config.format === 'pdf') {
      await generatePDF();
    } else {
      handlePrint();
    }
  };

  const getEntityName = () => {
    if (entityType === 'stakeholder') {
      const stakeholder = entity as Stakeholder;
      return `${stakeholder.firstName} ${stakeholder.lastName}`;
    } else {
      return (entity as Organization).name;
    }
  };

  const enabledCount = config ? config.sections.filter(s => s.enabled).length : 0;

  return (
    <>
      {/* Report Button */}
      <Button size="sm" variant="secondary" onClick={() => setShowConfigModal(true)}>
        <FileText className="h-4 w-4 text-neutral-500" strokeWidth={1.75} />
        Report
      </Button>

      {/* Configuration Modal */}
      <ReportConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onGenerate={handleConfigSubmit}
        entityType={entityType}
        entityName={getEntityName()}
      />

      {/* Preview Modal */}
      {config && (
        <Dialog
          open={showPreview}
          onClose={() => setShowPreview(false)}
          labelledBy="report-preview-title"
          handle
          surface="muted"
          className="h-[94vh] sm:h-auto sm:max-h-[90vh] sm:max-w-5xl"
        >
          <DialogHeader
            id="report-preview-title"
            eyebrow={<span className="block max-w-[260px] truncate sm:max-w-[560px]">{getEntityName()}</span>}
            title="Preview"
            onClose={() => setShowPreview(false)}
            className="pb-3"
          />

          <DialogBody className="px-3 pt-1 sm:px-5">
            {error && (
              <p
                role="alert"
                className="mb-3 flex items-center gap-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700"
              >
                <CircleAlert className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                {error}
              </p>
            )}

            <div className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white">
              <ReportPreview
                ref={reportRef}
                entity={entity}
                entityType={entityType}
                config={config}
                assignments={assignments}
                media={media}
                wikiPages={wikiPages}
                influenceScores={influenceScores}
                influenceCategories={influenceCategories}
                stakeholders={stakeholders}
              />
            </div>
          </DialogBody>

          <DialogFooter className="bg-white">
            <span className="mr-auto text-[12px] tabular-nums text-neutral-400">
              {enabledCount} {enabledCount === 1 ? 'section' : 'sections'}
            </span>
            <Button variant="secondary" onClick={() => setShowPreview(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleGenerate} loading={isGenerating}>
              {isGenerating ? (
                'Generating'
              ) : config.format === 'pdf' ? (
                <>
                  <Download className="h-4 w-4" strokeWidth={1.75} />
                  Download
                </>
              ) : (
                <>
                  <Printer className="h-4 w-4" strokeWidth={1.75} />
                  Print
                </>
              )}
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </>
  );
};
